import React, { useEffect, useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, Cell,
} from 'recharts';
import { api, EarningsPrediction, XGBoostPrediction, StoredPrediction } from '../api';
import { useAuth } from '../AuthContext';

export default function MLPredictions() {
  const { user } = useAuth();
  const isReader = user?.role === 'user-reader' || user?.role === 'reader';
  const [symbols, setSymbols] = useState<string[]>([]);
  const [symbol, setSymbol] = useState('');
  const [llmResult, setLlmResult] = useState<EarningsPrediction | null>(null);
  const [xgbResult, setXgbResult] = useState<XGBoostPrediction | null>(null);
  const [rfResult, setRfResult] = useState<XGBoostPrediction | null>(null);
  const [loadingLlm, setLoadingLlm] = useState(false);
  const [loadingXgb, setLoadingXgb] = useState(false);
  const [loadingRf, setLoadingRf] = useState(false);
  const [errorLlm, setErrorLlm] = useState('');
  const [errorXgb, setErrorXgb] = useState('');
  const [errorRf, setErrorRf] = useState('');
  const [horizon, setHorizon] = useState(5);

  // History state
  const [historyItems, setHistoryItems] = useState<StoredPrediction[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyModelFilter, setHistoryModelFilter] = useState<string>('all');

  useEffect(() => {
    const s = user?.stocks || [];
    setSymbols(s);
    if (s.length > 0) setSymbol(s[0]);
  }, [user?.stocks]);

  // Load cached results when symbol changes
  useEffect(() => {
    if (!symbol) return;
    setErrorLlm('');
    setErrorXgb('');
    setErrorRf('');
    try {
      const savedLlm = localStorage.getItem(`ml-compare-llm-${symbol}`);
      setLlmResult(savedLlm ? JSON.parse(savedLlm) : null);
    } catch { setLlmResult(null); }
    try {
      const savedXgb = localStorage.getItem(`ml-compare-xgb-${symbol}-${horizon}d`);
      setXgbResult(savedXgb ? JSON.parse(savedXgb) : null);
    } catch { setXgbResult(null); }
    try {
      const savedRf = localStorage.getItem(`ml-compare-rf-${symbol}-${horizon}d`);
      setRfResult(savedRf ? JSON.parse(savedRf) : null);
    } catch { setRfResult(null); }
  }, [symbol, horizon]);

  // Fetch prediction history when symbol changes or history panel opens
  useEffect(() => {
    if (!symbol || !historyOpen) return;
    setHistoryLoading(true);
    api.getPredictionHistory(symbol)
      .then((items) => {
        // Filter to only ML models (XGBoost, Random Forest)
        const mlItems = items.filter(
          (p) => p.model === 'XGBoost' || p.model === 'Random Forest'
        );
        setHistoryItems(mlItems);
      })
      .catch(() => setHistoryItems([]))
      .finally(() => setHistoryLoading(false));
  }, [symbol, historyOpen]);

  // Filtered history based on model selector
  const filteredHistory = useMemo(() => {
    if (historyModelFilter === 'all') return historyItems;
    return historyItems.filter((p) => p.model === historyModelFilter);
  }, [historyItems, historyModelFilter]);

  // Build history chart data: group by date, show XGBoost vs RF predicted prices
  const historyChartData = useMemo(() => {
    if (filteredHistory.length === 0) return [];

    const byDate = new Map<string, { date: string; xgboost?: number; rf?: number; xgboostDir?: string; rfDir?: string }>();
    for (const p of filteredHistory) {
      if (!p.date || p.date.length < 10) continue;
      const key = p.date;
      const entry = byDate.get(key) || { date: key };
      if (p.model === 'XGBoost') {
        entry.xgboost = p.predictedQuote;
        entry.xgboostDir = p.prediction?.direction;
      } else if (p.model === 'Random Forest') {
        entry.rf = p.predictedQuote;
        entry.rfDir = p.prediction?.direction;
      }
      byDate.set(key, entry);
    }

    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredHistory]);

  const runLlm = async () => {
    setLoadingLlm(true);
    setErrorLlm('');
    try {
      const r = await api.predictEarningsImpact(symbol);
      setLlmResult(r);
      try { localStorage.setItem(`ml-compare-llm-${symbol}`, JSON.stringify(r)); } catch {}
    } catch (err: any) {
      setErrorLlm(err.message || 'LLM prediction failed');
    } finally {
      setLoadingLlm(false);
    }
  };

  const runXgb = async () => {
    setLoadingXgb(true);
    setErrorXgb('');
    try {
      const r = await api.predictXGBoost(symbol, horizon);
      setXgbResult(r);
      try { localStorage.setItem(`ml-compare-xgb-${symbol}-${horizon}d`, JSON.stringify(r)); } catch {}
    } catch (err: any) {
      setErrorXgb(err.message || 'XGBoost prediction failed');
    } finally {
      setLoadingXgb(false);
    }
  };

  const runRf = async () => {
    setLoadingRf(true);
    setErrorRf('');
    try {
      const r = await api.predictRandomForest(symbol, horizon);
      setRfResult(r);
      try { localStorage.setItem(`ml-compare-rf-${symbol}-${horizon}d`, JSON.stringify(r)); } catch {}
    } catch (err: any) {
      setErrorRf(err.message || 'Random Forest prediction failed');
    } finally {
      setLoadingRf(false);
    }
  };

  const runBoth = () => { runLlm(); runXgb(); runRf(); };

  // Build comparison chart data from forecast array
  const comparisonData = useMemo(() => {
    if (!llmResult && !xgbResult && !rfResult) return [];

    // Use forecast array if available, else fall back to fiveDayForecast
    const xgbForecast = xgbResult?.forecast ?? [];
    const rfForecast = rfResult?.forecast ?? [];
    const llmDays = ['day1', 'day2', 'day3', 'day4', 'day5'] as const;

    // Determine the max horizon from ML models
    const allDays = new Set<number>();
    xgbForecast.forEach(f => allDays.add(f.day));
    rfForecast.forEach(f => allDays.add(f.day));
    if (llmResult) [1,2,3,4,5].forEach(d => allDays.add(d));
    const sortedDays = Array.from(allDays).sort((a, b) => a - b);

    return sortedDays.map((d) => {
      const xgbPt = xgbForecast.find(f => f.day === d);
      const rfPt = rfForecast.find(f => f.day === d);
      const llmKey = d <= 5 ? llmDays[d - 1] : undefined;
      return {
        day: `Day +${d}`,
        llmPattern: llmKey ? (llmResult?.patternPrediction?.[llmKey]?.predictedClose ?? null) : null,
        llmAdjusted: llmKey ? (llmResult?.newsAdjustedPrediction?.[llmKey]?.predictedClose ?? null) : null,
        xgboost: xgbPt?.predictedClose ?? null,
        randomForest: rfPt?.predictedClose ?? null,
        llmPatternPct: llmKey ? (llmResult?.patternPrediction?.[llmKey]?.pctChange ?? null) : null,
        llmAdjustedPct: llmKey ? (llmResult?.newsAdjustedPrediction?.[llmKey]?.pctChange ?? null) : null,
        xgboostPct: xgbPt?.pctChange ?? null,
        randomForestPct: rfPt?.pctChange ?? null,
      };
    });
  }, [llmResult, xgbResult, rfResult]);

  // Combined data with recent prices as baseline
  const fullChartData = useMemo(() => {
    const recent = (xgbResult?.recentPrices || rfResult?.recentPrices || llmResult?.recentPrices || []).map((p) => ({
      date: p.date,
      actual: p.close,
      llmPattern: null as number | null,
      llmAdjusted: null as number | null,
      xgboost: null as number | null,
      randomForest: null as number | null,
    }));

    if (recent.length > 0) {
      const lastPrice = recent[recent.length - 1].actual;
      recent[recent.length - 1] = {
        ...recent[recent.length - 1],
        llmPattern: lastPrice,
        llmAdjusted: lastPrice,
        xgboost: lastPrice,
        randomForest: lastPrice,
      };
    }

    // Use forecast array for dynamic horizon support
    const xgbForecast = xgbResult?.forecast ?? [];
    const rfForecast = rfResult?.forecast ?? [];
    const llmDays = ['day1', 'day2', 'day3', 'day4', 'day5'] as const;

    const allDays = new Set<number>();
    xgbForecast.forEach(f => allDays.add(f.day));
    rfForecast.forEach(f => allDays.add(f.day));
    if (llmResult) [1,2,3,4,5].forEach(d => allDays.add(d));
    const sortedDays = Array.from(allDays).sort((a, b) => a - b);

    const predictions = sortedDays.map((d) => {
      const xgbPt = xgbForecast.find(f => f.day === d);
      const rfPt = rfForecast.find(f => f.day === d);
      const llmKey = d <= 5 ? llmDays[d - 1] : undefined;
      return {
        date: `Day +${d}`,
        actual: null as number | null,
        llmPattern: llmKey ? (llmResult?.patternPrediction?.[llmKey]?.predictedClose ?? null) : null,
        llmAdjusted: llmKey ? (llmResult?.newsAdjustedPrediction?.[llmKey]?.predictedClose ?? null) : null,
        xgboost: xgbPt?.predictedClose ?? null,
        randomForest: rfPt?.predictedClose ?? null,
      };
    });

    return [...recent, ...predictions];
  }, [llmResult, xgbResult, rfResult]);

  // Feature importance data
  const featureData = useMemo(() => {
    if (!xgbResult?.topFeatures) return [];
    return xgbResult.topFeatures.map((f) => ({
      name: f.feature.replace(/_/g, ' '),
      importance: f.importance,
    }));
  }, [xgbResult]);

  const rfFeatureData = useMemo(() => {
    if (!rfResult?.topFeatures) return [];
    return rfResult.topFeatures.map((f) => ({
      name: f.feature.replace(/_/g, ' '),
      importance: f.importance,
    }));
  }, [rfResult]);

  const baseClose = Number(xgbResult?.lastClose || llmResult?.lastClose || 0);

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 20 }}>Model Predictions</h2>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
        <select
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          style={{ padding: '8px 12px', background: '#1e2130', color: '#e1e4eb', border: '1px solid #2a2d3a', borderRadius: 6 }}
        >
          {symbols.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={horizon}
          onChange={(e) => setHorizon(Number(e.target.value))}
          style={{ padding: '8px 12px', background: '#1e2130', color: '#e1e4eb', border: '1px solid #2a2d3a', borderRadius: 6 }}
        >
          <option value={1}>1 Day</option>
          <option value={5}>5 Days</option>
          <option value={10}>10 Days</option>
          <option value={20}>20 Days</option>
          <option value={30}>30 Days</option>
        </select>
        <button onClick={runBoth} disabled={isReader || loadingLlm || loadingXgb || loadingRf}
          style={{ padding: '8px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
          {isReader ? 'Reader role' : ((loadingLlm || loadingXgb || loadingRf) ? 'Running...' : 'Run All Models')}
        </button>
        <button onClick={runXgb} disabled={isReader || loadingXgb}
          style={{ padding: '8px 16px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
          {isReader ? 'Reader role' : (loadingXgb ? 'Training...' : 'XGBoost Only')}
        </button>
        <button onClick={runRf} disabled={isReader || loadingRf}
          style={{ padding: '8px 16px', background: '#f97316', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
          {isReader ? 'Reader role' : (loadingRf ? 'Training...' : 'Random Forest Only')}
        </button>
        <button onClick={runLlm} disabled={isReader || loadingLlm}
          style={{ padding: '8px 16px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
          {isReader ? 'Reader role' : (loadingLlm ? 'Predicting...' : 'LLM Only')}
        </button>
        {(llmResult || xgbResult || rfResult) && (
          <button onClick={() => {
            setLlmResult(null); setXgbResult(null); setRfResult(null);
            try { localStorage.removeItem(`ml-compare-llm-${symbol}`); localStorage.removeItem(`ml-compare-xgb-${symbol}-${horizon}d`); localStorage.removeItem(`ml-compare-rf-${symbol}-${horizon}d`); } catch {}
          }} style={{ padding: '6px 12px', background: 'transparent', color: '#8b8fa3', border: '1px solid #2a2d3a', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
            Clear
          </button>
        )}
      </div>

      {isReader && (
        <div className="error-msg" style={{ marginTop: -8 }}>
          Your role is reader. Prediction generation is disabled. Ask an admin to upgrade your access.
        </div>
      )}

      {(errorLlm || errorXgb || errorRf) && (
        <div style={{ color: '#ef4444', marginBottom: 16, fontSize: 13 }}>
          {errorLlm && <div>LLM Error: {errorLlm}</div>}
          {errorXgb && <div>XGBoost Error: {errorXgb}</div>}
          {errorRf && <div>Random Forest Error: {errorRf}</div>}
        </div>
      )}

      {/* Summary Cards */}
      {(llmResult || xgbResult || rfResult) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>
          {/* XGBoost Card */}
          <div style={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 12, padding: 20 }}>
            <h3 style={{ color: '#22c55e', marginBottom: 12, fontSize: 16 }}>🤖 XGBoost (ML)</h3>
            {xgbResult ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                  <div>Direction: <strong style={{ color: xgbResult.prediction.direction === 'up' ? '#22c55e' : '#ef4444' }}>
                    {xgbResult.prediction.direction === 'up' ? '↑ Up' : '↓ Down'}
                  </strong></div>
                  <div>Confidence: <strong>{xgbResult.prediction.confidence}</strong> ({(xgbResult.prediction.confidenceScore * 100).toFixed(1)}%)</div>
                  <div>Predicted: <strong>${xgbResult.prediction.predictedClose}</strong></div>
                  <div>Return: <strong style={{ color: xgbResult.prediction.predictedReturn >= 0 ? '#22c55e' : '#ef4444' }}>
                    {xgbResult.prediction.predictedReturn >= 0 ? '+' : ''}{xgbResult.prediction.predictedReturn.toFixed(2)}%
                  </strong></div>
                  <div>CV Accuracy: <strong>{(xgbResult.modelMetrics.cvAccuracy * 100).toFixed(1)}%</strong></div>
                  <div>Training: <strong>{xgbResult.modelMetrics.trainingRows.toLocaleString()}</strong> rows</div>
                  <div>Features: <strong>{xgbResult.modelMetrics.features}</strong></div>
                  <div>Model: <strong>{xgbResult.model}</strong></div>
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: '#8b8fa3' }}>
                  Generated {new Date(xgbResult.generatedAt).toLocaleString()}
                </div>
              </>
            ) : <div style={{ color: '#8b8fa3', fontSize: 13 }}>Not run yet</div>}
          </div>

          {/* Random Forest Card */}
          <div style={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 12, padding: 20 }}>
            <h3 style={{ color: '#f97316', marginBottom: 12, fontSize: 16 }}>🌲 Random Forest (ML)</h3>
            {rfResult ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                  <div>Direction: <strong style={{ color: rfResult.prediction.direction === 'up' ? '#22c55e' : '#ef4444' }}>
                    {rfResult.prediction.direction === 'up' ? '↑ Up' : '↓ Down'}
                  </strong></div>
                  <div>Confidence: <strong>{rfResult.prediction.confidence}</strong> ({(rfResult.prediction.confidenceScore * 100).toFixed(1)}%)</div>
                  <div>Predicted: <strong>${rfResult.prediction.predictedClose}</strong></div>
                  <div>Return: <strong style={{ color: rfResult.prediction.predictedReturn >= 0 ? '#22c55e' : '#ef4444' }}>
                    {rfResult.prediction.predictedReturn >= 0 ? '+' : ''}{rfResult.prediction.predictedReturn.toFixed(2)}%
                  </strong></div>
                  <div>CV Accuracy: <strong>{(rfResult.modelMetrics.cvAccuracy * 100).toFixed(1)}%</strong></div>
                  <div>Training: <strong>{rfResult.modelMetrics.trainingRows.toLocaleString()}</strong> rows</div>
                  <div>Features: <strong>{rfResult.modelMetrics.features}</strong></div>
                  <div>Model: <strong>{rfResult.model}</strong></div>
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: '#8b8fa3' }}>
                  Generated {new Date(rfResult.generatedAt).toLocaleString()}
                </div>
              </>
            ) : <div style={{ color: '#8b8fa3', fontSize: 13 }}>Not run yet</div>}
          </div>

          {/* LLM Card */}
          <div style={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 12, padding: 20 }}>
            <h3 style={{ color: '#8b5cf6', marginBottom: 12, fontSize: 16 }}>🧠 LLM (GPT-4o)</h3>
            {llmResult ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                  <div>Direction: <strong style={{
                    color: (llmResult.newsAdjustedPrediction?.day1?.pctChange ?? 0) >= 0 ? '#22c55e' : '#ef4444'
                  }}>
                    {(llmResult.newsAdjustedPrediction?.day1?.pctChange ?? 0) >= 0 ? '↑ Up' : '↓ Down'}
                  </strong></div>
                  <div>Confidence: <strong>{llmResult.confidence}</strong></div>
                  <div>Pattern Match: <strong>{llmResult.matchedPattern?.label}</strong></div>
                  <div>Patterns: <strong>{llmResult.patternsAnalyzed}</strong> analyzed</div>
                  <div>Day 1 Adj: <strong>${Number(llmResult.newsAdjustedPrediction?.day1?.predictedClose ?? 0).toFixed(2)}</strong></div>
                  <div>Model: <strong>{llmResult.model}</strong></div>
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: '#c4b5fd' }}>
                  {llmResult.overallSummary}
                </div>
                <div style={{ marginTop: 4, fontSize: 11, color: '#8b8fa3' }}>
                  Generated {new Date(llmResult.generatedAt).toLocaleString()}
                </div>
              </>
            ) : <div style={{ color: '#8b8fa3', fontSize: 13 }}>Not run yet</div>}
          </div>
        </div>
      )}

      {/* 5-Day Comparison Chart */}
      {fullChartData.length > 0 && (
        <div style={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16, fontSize: 15 }}>Price Forecast Comparison ({horizon}-Day Horizon)</h3>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={fullChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
              <XAxis dataKey="date" stroke="#8b8fa3" fontSize={12} />
              <YAxis stroke="#8b8fa3" fontSize={12} domain={['auto', 'auto']}
                tickFormatter={(v: number) => `$${v.toFixed(0)}`} />
              <Tooltip
                contentStyle={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 8, color: '#e1e4eb' }}
                formatter={(value: any, name: any) => value != null ? [`$${Number(value).toFixed(2)}`, name] : ['-', name]}
              />
              <Legend />
              <Line type="monotone" dataKey="actual" stroke="#6b7280" strokeWidth={2} name="Actual" dot={false} connectNulls={false} />
              <Line type="monotone" dataKey="xgboost" stroke="#22c55e" strokeWidth={2} name="XGBoost" dot={true} connectNulls={false} strokeDasharray="5 5" />
              <Line type="monotone" dataKey="randomForest" stroke="#f97316" strokeWidth={2} name="Random Forest" dot={true} connectNulls={false} strokeDasharray="5 5" />
              <Line type="monotone" dataKey="llmPattern" stroke="#f59e0b" strokeWidth={2} name="LLM Pattern" dot={true} connectNulls={false} strokeDasharray="5 5" />
              <Line type="monotone" dataKey="llmAdjusted" stroke="#8b5cf6" strokeWidth={2} name="LLM News-Adjusted" dot={true} connectNulls={false} strokeDasharray="5 5" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* % Change Comparison */}
      {comparisonData.length > 0 && (
        <div style={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16, fontSize: 15 }}>Predicted % Change from ${baseClose.toFixed(2)}</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2a2d3a' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: '#8b8fa3' }}>Day</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', color: '#22c55e' }}>XGBoost</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', color: '#f97316' }}>Random Forest</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', color: '#f59e0b' }}>LLM Pattern</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', color: '#8b5cf6' }}>LLM Adjusted</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row) => (
                  <tr key={row.day} style={{ borderBottom: '1px solid #1e2130' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{row.day}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: (Number(row.xgboostPct) ?? 0) >= 0 ? '#22c55e' : '#ef4444' }}>
                      {row.xgboostPct != null ? `${Number(row.xgboostPct) >= 0 ? '+' : ''}${Number(row.xgboostPct).toFixed(2)}%` : '-'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: (Number(row.randomForestPct) ?? 0) >= 0 ? '#22c55e' : '#ef4444' }}>
                      {row.randomForestPct != null ? `${Number(row.randomForestPct) >= 0 ? '+' : ''}${Number(row.randomForestPct).toFixed(2)}%` : '-'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: (Number(row.llmPatternPct) ?? 0) >= 0 ? '#22c55e' : '#ef4444' }}>
                      {row.llmPatternPct != null ? `${Number(row.llmPatternPct) >= 0 ? '+' : ''}${Number(row.llmPatternPct).toFixed(2)}%` : '-'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: (Number(row.llmAdjustedPct) ?? 0) >= 0 ? '#22c55e' : '#ef4444' }}>
                      {row.llmAdjustedPct != null ? `${Number(row.llmAdjustedPct) >= 0 ? '+' : ''}${Number(row.llmAdjustedPct).toFixed(2)}%` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Feature Importance */}
      {(featureData.length > 0 || rfFeatureData.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: featureData.length > 0 && rfFeatureData.length > 0 ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 24 }}>
          {featureData.length > 0 && (
            <div style={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 12, padding: 20 }}>
              <h3 style={{ marginBottom: 16, fontSize: 15 }}>XGBoost Top Features</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={featureData} layout="vertical" margin={{ left: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                  <XAxis type="number" stroke="#8b8fa3" fontSize={12} />
                  <YAxis type="category" dataKey="name" stroke="#8b8fa3" fontSize={11} width={100} />
                  <Tooltip
                    contentStyle={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 8, color: '#e1e4eb' }}
                    formatter={(v: any) => [Number(v).toFixed(4), 'Importance']}
                  />
                  <Bar dataKey="importance" radius={[0, 4, 4, 0]}>
                    {featureData.map((_, i) => (
                      <Cell key={i} fill={i < 3 ? '#22c55e' : i < 6 ? '#6366f1' : '#4b5563'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {rfFeatureData.length > 0 && (
            <div style={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 12, padding: 20 }}>
              <h3 style={{ marginBottom: 16, fontSize: 15 }}>Random Forest Top Features</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={rfFeatureData} layout="vertical" margin={{ left: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                  <XAxis type="number" stroke="#8b8fa3" fontSize={12} />
                  <YAxis type="category" dataKey="name" stroke="#8b8fa3" fontSize={11} width={100} />
                  <Tooltip
                    contentStyle={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 8, color: '#e1e4eb' }}
                    formatter={(v: any) => [Number(v).toFixed(4), 'Importance']}
                  />
                  <Bar dataKey="importance" radius={[0, 4, 4, 0]}>
                    {rfFeatureData.map((_, i) => (
                      <Cell key={i} fill={i < 3 ? '#f97316' : i < 6 ? '#6366f1' : '#4b5563'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* LLM Reasoning */}
      {llmResult?.newsAdjustmentReasoning && (
        <div style={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 12, padding: 20 }}>
          <h3 style={{ marginBottom: 12, fontSize: 15, color: '#8b5cf6' }}>LLM Reasoning</h3>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: '#c4b5fd' }}>
            <strong>Pattern Match:</strong> {llmResult.matchedPattern?.similarity}
          </p>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: '#e1e4eb', marginTop: 8 }}>
            <strong>News Adjustment:</strong> {llmResult.newsAdjustmentReasoning}
          </p>
        </div>
      )}

      {/* Prediction History */}
      <div style={{ marginTop: 32 }}>
        <button
          onClick={() => setHistoryOpen(!historyOpen)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
            background: '#1a1d27', color: '#e1e4eb', border: '1px solid #2a2d3a',
            borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 600, width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <span>📊 Prediction History ({symbol})</span>
          <span style={{ fontSize: 12, color: '#8b8fa3' }}>{historyOpen ? '▲ Collapse' : '▼ Expand'}</span>
        </button>

        {historyOpen && (
          <div style={{ marginTop: 16 }}>
            {/* Filter controls */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 13, color: '#8b8fa3' }}>Filter by model:</label>
              <select
                value={historyModelFilter}
                onChange={(e) => setHistoryModelFilter(e.target.value)}
                style={{ padding: '6px 10px', background: '#1e2130', color: '#e1e4eb', border: '1px solid #2a2d3a', borderRadius: 6, fontSize: 13 }}
              >
                <option value="all">All Models</option>
                <option value="XGBoost">XGBoost</option>
                <option value="Random Forest">Random Forest</option>
              </select>
              <span style={{ fontSize: 12, color: '#8b8fa3' }}>
                {historyLoading ? 'Loading...' : `${filteredHistory.length} predictions`}
              </span>
            </div>

            {/* History chart: predicted prices over time */}
            {historyChartData.length > 1 && (
              <div style={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <h3 style={{ marginBottom: 16, fontSize: 15 }}>Predicted Prices Over Time</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={historyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                    <XAxis dataKey="date" stroke="#8b8fa3" fontSize={11} tick={{ fill: '#8b8fa3' }}
                      tickFormatter={(v: string) => v.slice(5)} />
                    <YAxis stroke="#8b8fa3" fontSize={12} domain={['auto', 'auto']}
                      tickFormatter={(v: number) => `$${v.toFixed(0)}`} />
                    <Tooltip
                      contentStyle={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 8, color: '#e1e4eb' }}
                      formatter={(value: any, name: any) => value != null ? [`$${Number(value).toFixed(2)}`, name] : ['-', name]}
                    />
                    <Legend />
                    {(historyModelFilter === 'all' || historyModelFilter === 'XGBoost') && (
                      <Line type="monotone" dataKey="xgboost" stroke="#22c55e" strokeWidth={2} name="XGBoost" dot connectNulls={false} />
                    )}
                    {(historyModelFilter === 'all' || historyModelFilter === 'Random Forest') && (
                      <Line type="monotone" dataKey="rf" stroke="#f97316" strokeWidth={2} name="Random Forest" dot connectNulls={false} />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* History table */}
            {filteredHistory.length > 0 ? (
              <div style={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 12, padding: 20 }}>
                <h3 style={{ marginBottom: 16, fontSize: 15 }}>Past Predictions</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #2a2d3a' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', color: '#8b8fa3' }}>Date</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', color: '#8b8fa3' }}>Model</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', color: '#8b8fa3' }}>Horizon</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', color: '#8b8fa3' }}>Direction</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', color: '#8b8fa3' }}>Predicted</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', color: '#8b8fa3' }}>Return</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', color: '#8b8fa3' }}>Confidence</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', color: '#8b8fa3' }}>CV Accuracy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHistory.map((p) => (
                        <tr key={p.id} style={{ borderBottom: '1px solid #1e2130' }}>
                          <td style={{ padding: '8px 12px', fontWeight: 500 }}>{p.date}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{
                              color: p.model === 'XGBoost' ? '#22c55e' : '#f97316',
                              fontWeight: 600,
                            }}>
                              {p.model === 'XGBoost' ? '🤖' : '🌲'} {p.model}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>{p.timeframe}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <span style={{
                              color: p.prediction?.direction === 'up' ? '#22c55e' : '#ef4444',
                              fontWeight: 600,
                            }}>
                              {p.prediction?.direction === 'up' ? '↑ Up' : '↓ Down'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500 }}>
                            ${p.predictedQuote?.toFixed(2)}
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                            <span style={{
                              color: (p.prediction?.predictedReturn ?? 0) >= 0 ? '#22c55e' : '#ef4444',
                            }}>
                              {(p.prediction?.predictedReturn ?? 0) >= 0 ? '+' : ''}
                              {(p.prediction?.predictedReturn ?? 0).toFixed(2)}%
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                              background: p.prediction?.confidence === 'High' ? '#22c55e22' :
                                         p.prediction?.confidence === 'Medium' ? '#f59e0b22' : '#ef444422',
                              color: p.prediction?.confidence === 'High' ? '#22c55e' :
                                     p.prediction?.confidence === 'Medium' ? '#f59e0b' : '#ef4444',
                            }}>
                              {p.prediction?.confidence ?? '-'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                            {p.modelMetrics?.cvAccuracy != null
                              ? `${(p.modelMetrics.cvAccuracy * 100).toFixed(1)}%`
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : !historyLoading ? (
              <div style={{ background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 12, padding: 40, textAlign: 'center', color: '#8b8fa3' }}>
                No prediction history for {symbol}. Run predictions to start building history.
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
